import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { authClient, sendMagicLink } from "@/lib/auth";
import { emailHasPurchase } from "@/lib/auth-eligibility.functions";
import { CheckYourInbox } from "@/components/CheckYourInbox";


export const Route = createFileRoute("/auth/error")({
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : undefined,
    reason: typeof search.reason === "string" ? search.reason : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign-in link expired — WHO KNEW" },
      { name: "description", content: "Your sign-in link expired. Send another." },
    ],
  }),
  component: AuthError,
});

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';

function AuthError() {
  const { email: emailParam, reason } = Route.useSearch();
  const [email, setEmail] = useState(emailParam ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data } = await authClient.auth.getSession();
      if (!cancelled && data.session) {
        window.location.replace(
          "/courses/business-and-accountancy/basic-business-accountancy/lessons/1.1",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
      return;
    }
    try {
      const last = window.localStorage.getItem("last_signed_in_email");
      if (last) setEmail(last);
    } catch {
      // Ignore unavailable browser storage.
    }
  }, [emailParam]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    // Same gate as /signin: don't issue a fresh magic link to a non-customer.
    try {
      const eligibility = await emailHasPurchase({
        data: { email: email.trim() },
      });
      if (!eligibility.eligible) {
        setSubmitting(false);
        setError("not_a_customer");
        return;
      }
    } catch (err) {
      console.error("[auth-error] eligibility check failed:", err);
    }
    const res = await sendMagicLink(email);
    setSubmitting(false);
    if (res.ok) setSentTo(email.trim());
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />
      <main className="flex flex-1 px-6 pt-[80px] md:pt-0">
        <div
          className="mx-auto w-full max-w-[720px]"
          style={{ paddingTop: "64px", paddingBottom: "96px" }}
        >
          {sentTo ? (
            <CheckYourInbox
              email={sentTo}
              onTryDifferent={() => {
                setSentTo(null);
                setEmail("");
              }}
            />
          ) : (
            <>
              <p
                className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                style={{ fontFamily: MONO }}
              >
                SIGN-IN LINK EXPIRED
              </p>
              <h1
                className="text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink"
                style={{ marginTop: "24px" }}
              >
                Let's get you another one.
              </h1>
              <p
                className="text-[18px] leading-[1.5] text-ink"
                style={{ fontFamily: SERIF, marginTop: "24px" }}
              >
                Sign-in links expire after 60 minutes for security. Just send another.
              </p>
              <hr
                className="border-0 h-[2px] bg-burgundy"
                style={{ marginTop: "32px", width: "60px" }}
              />
              <form onSubmit={onSubmit} style={{ marginTop: "32px" }}>
                <label
                  className="block font-bold uppercase tracking-[0.05em]"
                  style={{
                    fontFamily: MONO,
                    fontSize: "10px",
                    color: "#5A534A",
                    marginBottom: "8px",
                  }}
                >
                  EMAIL
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="block w-full bg-paper-dark px-5 py-4 text-[16px] text-ink placeholder:text-muted-foreground focus:outline-none"
                  style={{ fontFamily: MONO }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-4 bg-burgundy text-paper text-[14px] font-bold uppercase tracking-[0.05em] transition-colors hover:bg-[#6B1717] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: MONO, padding: "18px 32px" }}
                >
                  {submitting ? "SENDING…" : "SEND MY MAGIC LINK"}
                </button>
                {error === "not_a_customer" ? (
                  <p
                    className="mt-4 text-[14px] italic"
                    style={{ color: "#5A534A", fontFamily: SERIF }}
                  >
                    We don't recognise that email. If you'd like to take the
                    course,{" "}
                    <Link
                      to="/"
                      className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
                    >
                      head to the homepage
                    </Link>{" "}
                    to get started.
                  </p>
                ) : error ? (
                  <p
                    className="mt-4 text-[14px] italic"
                    style={{ color: "#5A534A", fontFamily: SERIF }}
                  >
                    {error}
                  </p>
                ) : null}
              </form>
            </>
          )}
        </div>
      </main>
      <footer className="flex w-full flex-col items-center">
        <hr className="border-0 h-px bg-burgundy w-full max-w-[720px]" />
        <p
          className="mt-6 font-bold uppercase tracking-[0.1em] text-burgundy text-[16px] text-center"
          style={{ fontFamily: MONO }}
        >
          WHO KNEW
        </p>
        <div
          className="mt-4 mb-6 font-bold uppercase tracking-[0.05em] text-[10px] text-burgundy text-center"
          style={{ fontFamily: MONO }}
        >
          <Link to="/privacy" className="link-hit link-hit-tight hover:underline">
            Privacy
          </Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="link-hit link-hit-tight hover:underline">
            Terms
          </Link>
          <span className="mx-2">·</span>
          <Link to="/refunds" className="link-hit link-hit-tight hover:underline">
            Refunds
          </Link>
          <span className="mx-2">·</span>
          <Link to="/teams" className="link-hit link-hit-tight hover:underline">
            For teams
          </Link>
        </div>

      </footer>
    </div>
  );
}
